import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  MenuItem,
  Typography,
  Alert,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  PersonAdd as PersonAddIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  HelpOutline as HelpOutlineIcon,
  Group as GroupIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/apiService';
import { useAuth } from '../context/AuthContext';
import { useProjects } from '../context/ProjectContext';
import { getHelpPathForPage } from '../utils/helpPageMapping';
import { canManageProject } from '../utils/permissions';
import { formatAppTimestamp, parseApiDateTime } from '../utils/dateTimeUtils';
import { DFT_BRAND, zoomedMainMinHeight } from '../styles/brandPalette';

/** Match Collections / Dashboard chrome: canvas + inner 14px cards. */
const PROJECTS_CARD_RADIUS = '14px';
const projectsPageSx = {
  width: '100%',
  maxWidth: 'none',
  minWidth: 0,
  alignSelf: 'stretch',
  flex: 1,
  px: 0,
  py: 0,
  bgcolor: 'transparent',
  display: 'flex',
  flexDirection: 'column',
  overflowX: 'hidden',
  boxSizing: 'border-box' as const,
  minHeight: {
    xs: 'calc(100dvh - 64px)',
    ...zoomedMainMinHeight(),
  },
  '& .MuiCard-root': {
    borderRadius: `${PROJECTS_CARD_RADIUS} !important`,
    overflow: 'hidden',
  },
  '& .MuiAlert-root': {
    borderRadius: '12px',
  },
  '& .MuiTypography-subtitle2': {
    fontSize: '0.875rem',
    lineHeight: 1.45,
  },
  '& .MuiTypography-body2': {
    fontSize: '0.875rem',
    lineHeight: 1.45,
  },
} as const;
const PROJECTS_WORKSPACE_SX = {
  flex: '1 1 auto',
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '0 12px 12px 12px',
  border: `1px solid ${DFT_BRAND.line}`,
  borderTop: `1px solid ${DFT_BRAND.line}`,
  bgcolor: DFT_BRAND.bg,
  boxShadow: 'none',
  overflow: 'visible',
  p: { xs: 1.25, sm: 1.5 },
} as const;
const projectsCardSx = {
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  overflow: 'hidden',
  position: 'relative' as const,
  border: `1px solid ${DFT_BRAND.line}`,
  borderRadius: `${PROJECTS_CARD_RADIUS} !important`,
  boxShadow: '0 1px 2px rgba(23, 36, 65, 0.04)',
  bgcolor: DFT_BRAND.paper,
} as const;

interface Project {
  id: number;
  name: string;
  description: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

interface ProjectMemberAssignment {
  userId: number;
  name: string;
  email: string;
  projectCount: number;
}

interface CreateProjectData {
  name: string;
  description: string;
  createdBy: string;
}

export const PROJECT_NAME_MAX_LENGTH = 30;
/** Header project dropdown width — fits full 30-character project names. */
export const PROJECT_SELECT_CONTROL_WIDTH = { xs: 280, sm: 320 };

/** Strip leading spaces and collapse runs of spaces while the user is typing. */
function sanitizeProjectNameWhileTyping(name: string): string {
  return name.replace(/^\s+/, '').replace(/ {2,}/g, ' ').slice(0, PROJECT_NAME_MAX_LENGTH);
}

/** Canonical name persisted on save (trim ends, single spaces between words). */
function normalizeProjectNameForSave(name: string): string {
  return name.trim().replace(/\s+/g, ' ').slice(0, PROJECT_NAME_MAX_LENGTH);
}

function projectNameCharCount(name: string): number {
  return normalizeProjectNameForSave(name).length;
}

const Projects: React.FC = () => {
  const navigate = useNavigate();
  const { projects, refreshProjects, selectedProject, setSelectedProject, isLoading: contextLoading } = useProjects();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Create Project Dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newProject, setNewProject] = useState<CreateProjectData>({
    name: '',
    description: '',
    createdBy: '',
  });
  
  // Edit Project Dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [editForm, setEditForm] = useState<CreateProjectData>({
    name: '',
    description: '',
    createdBy: '',
  });
  
  const [membersDialogOpen, setMembersDialogOpen] = useState(false);
  const [membersDialogProject, setMembersDialogProject] = useState<Project | null>(null);
  const [projectMembers, setProjectMembers] = useState<ProjectMemberAssignment[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignProjectId, setAssignProjectId] = useState<number | null>(null);
  const [assignEmail, setAssignEmail] = useState('');
  const [assignableEmails, setAssignableEmails] = useState<string[]>([]);
  const [assignOptionsLoading, setAssignOptionsLoading] = useState(false);

  // Track which project is currently active (used for Work Progress indicator)
  const [activeProjectId, setActiveProjectId] = useState<number | null>(null);

  const { user } = useAuth();

  // Get user's full name
  const getUserFullName = useCallback(() => {
    if (!user) {
      return '';
    }
    
    // Handle camelCase and snake_case (API/localStorage may use either)
    const u = user as { firstName?: string; lastName?: string; first_name?: string; last_name?: string; email?: string };
    const firstName = (u.firstName || u.first_name || '').trim();
    const lastName = (u.lastName || u.last_name || '').trim();
    
    // Build full name
    const fullName = `${firstName} ${lastName}`.trim();
    
    // If we have a name, return it
    if (fullName) {
      return fullName;
    }
    
    // Fallback to email (should always be present)
    const email = (u.email || '').trim();
    if (email) {
      return email;
    }
    
    // Last resort when user has no name/email (e.g. from token exchange before backend fix)
    return 'Signed-in user';
  }, [user]);

  // Projects are now managed by ProjectContext, no need for local fetchProjects
  // The context will automatically fetch projects when user is authenticated

  // Only Project Manager or Admin + Project Manager can create or update projects. Members can view only.
  const canManageProjects = canManageProject(user?.role);
  const canManageAssignments = user?.role === 'PROJECT_MANAGER' || user?.role === 'ADMIN_PROJECT_MANAGER';
  const canViewMembers = user?.role !== 'ADMIN';

  // Update createdBy when dialog opens or user changes
  useEffect(() => {
    if (createDialogOpen && user) {
      const fullName = getUserFullName();
      setNewProject(prev => ({ ...prev, createdBy: fullName }));
    }
  }, [createDialogOpen, user, getUserFullName]);

  // Restore active project from localStorage on load
  useEffect(() => {
    const storedProjectId = localStorage.getItem('activeProjectId');
    if (storedProjectId) {
      const parsed = Number(storedProjectId);
      if (!Number.isNaN(parsed)) {
        setActiveProjectId(parsed);
      }
    }
  }, []);

  // Keep Work Progress icon in sync with the currently selected project in context
  useEffect(() => {
    if (selectedProject && selectedProject.id !== activeProjectId) {
      const id = selectedProject.id;
      setActiveProjectId(id);
      localStorage.setItem('activeProjectId', String(id));
    }
  }, [selectedProject, activeProjectId]);

  // Open a project and mark it as active + selected in context
  const openProject = (projectId: number) => {
    setActiveProjectId(projectId);
    localStorage.setItem('activeProjectId', String(projectId));

    const project = projects.find((p) => p.id === projectId) || null;
    if (project) {
      setSelectedProject(project);
    }

    navigate(`/collections?projectId=${projectId}`);
  };

  const handleCreateProject = async () => {
    const normalizedName = normalizeProjectNameForSave(newProject.name);
    if (!normalizedName) {
      setError('Project name is required');
      return;
    }
    if (normalizedName.length > PROJECT_NAME_MAX_LENGTH) {
      setError(`Project name must be ${PROJECT_NAME_MAX_LENGTH} characters or fewer`);
      return;
    }

    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    try {
      await apiService.createProject({ ...newProject, name: normalizedName });
      // Refresh projects from context to get the updated list
      await refreshProjects();
      setCreateDialogOpen(false);
      setNewProject({ name: '', description: '', createdBy: getUserFullName() });
      setSuccessMessage('Project has been created successfully.');
    } catch (err: any) {
      const msg = err.response?.data?.error || err.response?.data?.message || 'Failed to create project';
      setError(msg);
      console.error('Error creating project:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditProject = async () => {
    const normalizedName = normalizeProjectNameForSave(editForm.name);
    if (!editingProject || !normalizedName) {
      setError('Project name is required');
      return;
    }
    if (normalizedName.length > PROJECT_NAME_MAX_LENGTH) {
      setError(`Project name must be ${PROJECT_NAME_MAX_LENGTH} characters or fewer`);
      return;
    }

    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    try {
      await apiService.updateProject(editingProject.id, { ...editForm, name: normalizedName });
      // Refresh projects from context to get the updated list
      await refreshProjects();
      setEditDialogOpen(false);
      setEditingProject(null);
      setEditForm({ name: '', description: '', createdBy: getUserFullName() });
    } catch (err: any) {
      const msg = err.response?.data?.error || err.response?.data?.message || 'Failed to update project';
      setError(msg);
      console.error('Error updating project:', err);
    } finally {
      setLoading(false);
    }
  };

  const openAssignUserDialog = async (projectId: number) => {
    setAssignProjectId(projectId);
    setAssignEmail('');
    setAssignDialogOpen(true);
    setAssignOptionsLoading(true);
    setError(null);
    try {
      const emails = await apiService.getAssignableProjectMembers(projectId);
      const ownEmail = user?.email?.trim().toLowerCase() ?? '';
      setAssignableEmails(
        (Array.isArray(emails) ? emails : []).filter((e) => e.trim().toLowerCase() !== ownEmail)
      );
    } catch (err: any) {
      const msg = err.response?.data?.error || err.response?.data?.message || 'Failed to load member emails';
      setError(msg);
      setAssignableEmails([]);
    } finally {
      setAssignOptionsLoading(false);
    }
  };

  const handleAssignUserToProject = async () => {
    if (!assignProjectId) return;
    const email = assignEmail.trim();
    if (!email || !email.includes('@')) {
      setError('Please select a valid member email');
      return;
    }
    try {
      setError(null);
      await apiService.assignUserToProject(assignProjectId, email);
      setSuccessMessage('User assigned to project successfully.');
      setAssignDialogOpen(false);
      setAssignProjectId(null);
      setAssignEmail('');
      if (membersDialogOpen && membersDialogProject?.id === assignProjectId) {
        await fetchProjectMembers(assignProjectId);
      }
    } catch (err: any) {
      const msg = err.response?.data?.error || err.response?.data?.message || 'Failed to assign user to project';
      setError(msg);
    }
  };

  const fetchProjectMembers = async (projectId: number) => {
    setMembersLoading(true);
    try {
      const members = await apiService.getProjectMembers(projectId);
      setProjectMembers(members || []);
    } catch (err: any) {
      const msg = err.response?.data?.error || err.response?.data?.message || 'Failed to load project members';
      setError(msg);
      setProjectMembers([]);
    } finally {
      setMembersLoading(false);
    }
  };

  const openMembersDialog = async (project: Project) => {
    setMembersDialogProject(project);
    setMembersDialogOpen(true);
    setError(null);
    await fetchProjectMembers(project.id);
  };

  const handleRemoveMemberFromProject = async (projectId: number, userId: number) => {
    try {
      await apiService.removeProjectMember(projectId, userId);
      setSuccessMessage('Member removed from project successfully.');
      await fetchProjectMembers(projectId);
    } catch (err: any) {
      const msg = err.response?.data?.error || err.response?.data?.message || 'Failed to remove member from project';
      setError(msg);
    }
  };

  const openEditDialog = (project: Project) => {
    setEditingProject(project);
    setEditForm({
      name: project.name,
      description: project.description,
      createdBy: project.createdBy,
    });
    setEditDialogOpen(true);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredProjects = projects
    .filter((project) =>
      project.name.toLowerCase().includes(searchQuery.toLowerCase().trim())
    )
    .sort((a, b) => {
      const dateA = parseApiDateTime(a.createdAt)?.getTime() ?? 0;
      const dateB = parseApiDateTime(b.createdAt)?.getTime() ?? 0;
      if (dateA !== dateB) return dateA - dateB;
      return a.id - b.id;
    });

  return (
    <Box sx={projectsPageSx}>
      <Box sx={{ width: '100%', flexShrink: 0, overflow: 'visible' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            gap: 1,
            minWidth: 0,
            px: { xs: 1.25, sm: 1.5 },
            pt: 1.25,
            pb: 0.5,
            minHeight: 36,
          }}
        >
          <Typography
            variant="h5"
            component="h1"
            sx={{
              m: 0,
              fontWeight: 700,
              height: 36,
              lineHeight: '36px',
              display: 'flex',
              alignItems: 'center',
              letterSpacing: '-0.02em',
              color: DFT_BRAND.text,
            }}
          >
            Projects
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              minHeight: 36,
              '& .MuiButton-root': {
                display: 'inline-flex',
                alignItems: 'center',
                lineHeight: 1,
              },
              '& .MuiButton-startIcon': {
                display: 'inline-flex',
                alignItems: 'center',
                marginTop: 0,
                marginBottom: 0,
              },
            }}
          >
            <Button
              size="small"
              variant="outlined"
              startIcon={<HelpOutlineIcon />}
              onClick={() => navigate(getHelpPathForPage('PROJECTS'))}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.875rem',
                color: '#1d4ed8',
                borderColor: '#bfdbfe',
                bgcolor: '#ffffff',
                height: 36,
                minHeight: 36,
                borderRadius: 0,
                px: 1.1,
                whiteSpace: 'nowrap',
                '&:hover': {
                  borderColor: '#93c5fd',
                  bgcolor: '#eff6ff',
                },
              }}
              aria-label="Open help for Projects"
            >
              Help
            </Button>
            {canManageProjects && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => {
                  setError(null);
                  setNewProject({ name: '', description: '', createdBy: getUserFullName() });
                  setCreateDialogOpen(true);
                }}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  height: 36,
                  minHeight: 36,
                  borderRadius: 1.5,
                  bgcolor: '#1d4ed8',
                  '&:hover': { bgcolor: '#1e40af' },
                  px: 1.2,
                  fontSize: '0.875rem',
                  boxShadow: 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                Create Project
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      <Box sx={PROJECTS_WORKSPACE_SX}>
      {error && !createDialogOpen && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {!canManageProjects && user && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Only Project Manager or Admin + Project Manager can create or edit projects. You can view projects only.
        </Alert>
      )}

      <Card elevation={0} sx={projectsCardSx}>
        <CardContent sx={{ width: '100%', maxWidth: '100%', minWidth: 0, p: '12px !important' }}>
          <TextField
            fullWidth
            size="small"
            label="Search projects by name"
            placeholder="Type project name..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(0);
            }}
            sx={{
              mb: 1.5,
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
                bgcolor: '#fff',
                height: 40,
                fontSize: '0.875rem',
                '& fieldset': { borderColor: DFT_BRAND.line },
              },
            }}
          />
          <TableContainer 
            component={Paper} 
            className="projects-table-container"
            sx={{ 
              boxShadow: 'none',
              width: '100%',
              maxWidth: '100%',
              minWidth: 0,
              overflowX: 'auto',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            <Table 
              className="projects-table"
              sx={{ 
                width: '100%',
                minWidth: { xs: 520, md: '100%' },
                tableLayout: 'fixed',
                '& .MuiTableCell-root': {
                  fontSize: '0.875rem',
                  lineHeight: 1.45,
                },
              }}
            >
              <colgroup>
                <col style={{ width: '24%' }} />
                <Box
                  component="col"
                  sx={{
                    display: { xs: 'none', md: 'table-column' },
                    width: { md: 'auto' },
                  }}
                />
                <Box
                  component="col"
                  sx={{
                    display: { xs: 'none', xl: 'table-column' },
                    width: { xl: '14%' },
                  }}
                />
                <Box
                  component="col"
                  sx={{
                    display: { xs: 'none', sm: 'table-column' },
                    width: { sm: '16%' },
                  }}
                />
                <col style={{ width: 164 }} />
              </colgroup>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', color: DFT_BRAND.text, borderColor: DFT_BRAND.line, bgcolor: DFT_BRAND.tint, py: 1 }}>Project Name</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', color: DFT_BRAND.text, borderColor: DFT_BRAND.line, bgcolor: DFT_BRAND.tint, py: 1, display: { xs: 'none', md: 'table-cell' } }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', color: DFT_BRAND.text, borderColor: DFT_BRAND.line, bgcolor: DFT_BRAND.tint, py: 1, display: { xs: 'none', xl: 'table-cell' } }}>Created By</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', color: DFT_BRAND.text, borderColor: DFT_BRAND.line, bgcolor: DFT_BRAND.tint, py: 1, display: { xs: 'none', sm: 'table-cell' } }}>Created</TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      color: DFT_BRAND.text,
                      borderColor: DFT_BRAND.line,
                      bgcolor: DFT_BRAND.tint,
                      width: 164,
                      minWidth: 164,
                      maxWidth: 164,
                      py: 1,
                      pr: 1,
                      pl: 0.5,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(contextLoading || loading) && filteredProjects.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography variant="body2" color="text.secondary">
                        Loading projects...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : filteredProjects.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography variant="body2" color="text.secondary">
                        {projects.length === 0
                          ? 'No projects found. Create your first project to get started.'
                          : 'No projects match your search.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredProjects
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((project) => (
                      <TableRow
                        key={project.id}
                        onClick={() => openProject(project.id)}
                        sx={{
                          cursor: 'pointer',
                          backgroundColor: DFT_BRAND.paper,
                          '&:hover': {
                            backgroundColor: DFT_BRAND.tint,
                          },
                          '& td': { borderColor: DFT_BRAND.line },
                        }}
                      >
                      <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 0, py: 1 }}>
                        <Typography
                          variant="subtitle2"
                          title={project.name}
                          sx={{
                            fontWeight: 500,
                            fontSize: '0.875rem',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            cursor: 'pointer',
                          }}
                        >
                          {project.name}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ wordBreak: 'break-word', whiteSpace: 'normal', maxWidth: 0, py: 1, display: { xs: 'none', md: 'table-cell' } }}>
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                          }}
                        >
                          {project.description || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', py: 1, display: { xs: 'none', xl: 'table-cell' } }}>
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          title={project.createdBy}
                          sx={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                        >
                          {project.createdBy}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', py: 1, display: { xs: 'none', sm: 'table-cell' } }}>
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                        >
                          {formatAppTimestamp(project.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          textAlign: 'right',
                          px: 0.5,
                          pr: 1,
                          width: 164,
                          minWidth: 164,
                          maxWidth: 164,
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                          verticalAlign: 'middle',
                          backgroundColor: 'transparent',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'inline-flex',
                            gap: 0.25,
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            flexWrap: 'nowrap',
                            width: '100%',
                          }}
                        >
                          <Tooltip title={activeProjectId === project.id ? 'Working project' : 'Set as working project'}>
                            <Box
                              component="img"
                              src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRLHpTNTrknWG5sgnx9DGUfnbZ_KyVOP0nURA&s"
                              alt="Work progress"
                              sx={{
                                width: 28,
                                height: 28,
                                display: 'block',
                                flexShrink: 0,
                                imageRendering: 'crisp-edges',
                                filter: activeProjectId === project.id ? 'hue-rotate(90deg) saturate(1.6)' : 'none',
                                backgroundColor: 'transparent',
                                boxShadow: 'none',
                                borderRadius: 0,
                              }}
                            />
                          </Tooltip>
                        <Box sx={{ display: 'flex', gap: 0.125, ml: 0, flexShrink: 0 }}>
                          {canManageProjects && (
                            <>
                              {canManageAssignments && (
                                <IconButton
                                  size="small"
                                  color="secondary"
                                  sx={{ width: 32, height: 32, p: 0 }}
                                  onClick={(e) => { e.stopPropagation(); void openAssignUserDialog(project.id); }}
                                  title="Assign Member"
                                >
                                  <PersonAddIcon fontSize="small" />
                                </IconButton>
                              )}
                              {canViewMembers && (
                                <IconButton
                                  size="small"
                                  color="info"
                                  sx={{ width: 32, height: 32, p: 0 }}
                                  onClick={(e) => { e.stopPropagation(); openMembersDialog(project); }}
                                  title="View Members"
                                >
                                  <GroupIcon fontSize="small" />
                                </IconButton>
                              )}
                              <IconButton
                                size="small"
                                color="primary"
                                sx={{ width: 32, height: 32, p: 0 }}
                                onClick={(e) => { e.stopPropagation(); openEditDialog(project); }}
                                title="Edit Project"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </>
                          )}
                          {!canManageProjects && canViewMembers && (
                            <IconButton
                              size="small"
                              color="info"
                              sx={{ width: 32, height: 32, p: 0 }}
                              onClick={(e) => { e.stopPropagation(); openMembersDialog(project); }}
                              title="View Members"
                            >
                              <GroupIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                        </Box>
                      </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={filteredProjects.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              color: DFT_BRAND.muted,
              borderTop: `1px solid ${DFT_BRAND.line}`,
              overflow: 'hidden',
              '& .MuiTablePagination-toolbar': {
                flexWrap: 'wrap',
                minHeight: 36,
                px: 0.5,
                gap: 0.5,
              },
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                color: DFT_BRAND.muted,
                fontSize: '0.8125rem',
                m: 0,
              },
              '& .MuiTablePagination-select': {
                fontSize: '0.8125rem',
              },
              '& .MuiTablePagination-actions .MuiIconButton-root': {
                p: 0.5,
              },
            }}
          />
        </CardContent>
      </Card>
      </Box>

      {/* Create Project Dialog */}
      <Dialog 
        open={createDialogOpen} 
        onClose={() => {
          setCreateDialogOpen(false);
          setError(null);
        }} 
        maxWidth={false}
        sx={{
          '& .MuiDialog-container': {
            alignItems: 'center',
            justifyContent: 'center',
          },
        }}
        PaperProps={{
          sx: {
            width: { xs: 'calc(100% - 32px)', sm: 440 },
            maxWidth: 'min(92vw, 520px)',
            maxHeight: 'calc(100vh - 160px)',
            m: 2,
            borderRadius: '14px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }
        }}
      >
        <DialogTitle sx={{ pb: 1.25, fontWeight: 700 }}>
          Create New Project
        </DialogTitle>
        <DialogContent sx={{ pt: 1.5, overflowY: 'auto' }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Project Name"
            fullWidth
            variant="outlined"
            value={newProject.name}
            onChange={(e) =>
              setNewProject((prev) => ({ ...prev, name: sanitizeProjectNameWhileTyping(e.target.value) }))
            }
            onBlur={() =>
              setNewProject((prev) => ({ ...prev, name: normalizeProjectNameForSave(prev.name) }))
            }
            sx={{ mb: 1.5 }}
            size="small"
            helperText={`${projectNameCharCount(newProject.name)}/${PROJECT_NAME_MAX_LENGTH}`}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={newProject.description}
            onChange={(e) => setNewProject(prev => ({ ...prev, description: e.target.value }))}
            sx={{ mb: 1.5 }}
            size="small"
          />
          <TextField
            margin="dense"
            label="Created By"
            fullWidth
            variant="outlined"
            value={newProject.createdBy}
            InputProps={{
              readOnly: true,
            }}
            sx={{ mb: 1 }}
            size="small"
            helperText="This field is automatically set to your name"
          />
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            pb: 2.25,
            pt: 1.25,
            gap: 1,
            justifyContent: 'flex-end',
            '& > .MuiButton-root': { minWidth: 120 },
          }}
        >
          <Button
            onClick={() => {
              setCreateDialogOpen(false);
              setError(null);
            }}
            startIcon={<CancelIcon />}
            variant="text"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleCreateProject} 
            variant="contained" 
            startIcon={<SaveIcon />}
            disabled={loading || !normalizeProjectNameForSave(newProject.name)}
          >
            {loading ? 'Creating...' : 'Create Project'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Project Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)} 
        maxWidth={false}
        PaperProps={{
          sx: {
            width: { xs: 'calc(100% - 32px)', sm: 440 },
            maxWidth: 'min(92vw, 520px)',
            m: 2,
            borderRadius: '14px',
            overflow: 'hidden',
          }
        }}
      >
        <DialogTitle sx={{ pb: 1.25, fontWeight: 700 }}>
          Edit Project
        </DialogTitle>
        <DialogContent sx={{ pt: 1.5 }}>
          <TextField
            autoFocus
            margin="dense"
            label="Project Name"
            fullWidth
            variant="outlined"
            value={editForm.name}
            onChange={(e) =>
              setEditForm((prev) => ({ ...prev, name: sanitizeProjectNameWhileTyping(e.target.value) }))
            }
            onBlur={() =>
              setEditForm((prev) => ({ ...prev, name: normalizeProjectNameForSave(prev.name) }))
            }
            sx={{ mb: 1.5 }}
            size="small"
            helperText={`${projectNameCharCount(editForm.name)}/${PROJECT_NAME_MAX_LENGTH}`}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={editForm.description}
            onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
            sx={{ mb: 1.5 }}
            size="small"
          />
          <TextField
            margin="dense"
            label="Created By"
            fullWidth
            variant="outlined"
            value={editForm.createdBy}
            InputProps={{
              readOnly: true,
            }}
            sx={{ mb: 1 }}
            size="small"
            helperText="This field cannot be changed"
          />
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            pb: 2.25,
            pt: 1.25,
            gap: 1,
            justifyContent: 'flex-end',
            '& > .MuiButton-root': { minWidth: 120 },
          }}
        >
          <Button onClick={() => setEditDialogOpen(false)} startIcon={<CancelIcon />} variant="text">
            Cancel
          </Button>
          <Button 
            onClick={handleEditProject} 
            variant="contained" 
            startIcon={<SaveIcon />}
            disabled={loading || !normalizeProjectNameForSave(editForm.name)}
          >
            {loading ? 'Updating...' : 'Update Project'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={assignDialogOpen}
        onClose={() => setAssignDialogOpen(false)}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: { xs: 'calc(100% - 32px)', sm: 440 },
            maxWidth: 'min(92vw, 520px)',
            m: 2,
            borderRadius: '14px',
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle>Assign Member</DialogTitle>
        <DialogContent sx={{ overflowX: 'hidden' }}>
          {assignOptionsLoading ? (
            <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={24} />
            </Box>
          ) : assignableEmails.length === 0 ? (
            <Alert severity="info" sx={{ mt: 1 }}>
              No team members are available to assign to this project. Eligible members may already be
              assigned, or there are no assignable members in your organization.
            </Alert>
          ) : (
            <TextField
              select
              fullWidth
              margin="dense"
              label="Member Email"
              value={assignEmail}
              onChange={(e) => setAssignEmail(e.target.value)}
              helperText="Select a member email"
            >
              {assignableEmails.map((email) => (
                <MenuItem key={email} value={email}>
                  {email}
                </MenuItem>
              ))}
            </TextField>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Button onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAssignUserToProject}
            disabled={assignOptionsLoading || !assignEmail.trim()}
          >
            Assign
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={membersDialogOpen}
        onClose={() => setMembersDialogOpen(false)}
        maxWidth={false}
        scroll="paper"
        PaperProps={{
          sx: {
            width: { xs: 'calc(100% - 32px)', sm: 720 },
            maxWidth: 'min(92vw, 900px)',
            maxHeight: 'min(calc(100vh - 32px), calc(100dvh - 32px))',
            m: 2,
            borderRadius: '14px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle sx={{ flexShrink: 0 }}>
          View Members - {membersDialogProject?.name || 'Project'}
        </DialogTitle>
        <DialogContent sx={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
          {membersLoading ? (
            <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={28} />
            </Box>
          ) : projectMembers.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
              No members assigned
            </Typography>
          ) : (
            <TableContainer component={Paper} sx={{ mt: 1, maxWidth: '100%', overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Project Count</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {projectMembers.map((member) => (
                    <TableRow key={member.userId}>
                      <TableCell>{member.name || '-'}</TableCell>
                      <TableCell>{member.email}</TableCell>
                      <TableCell>{member.projectCount ?? 0}</TableCell>
                      <TableCell>
                        {canManageAssignments ? (
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={() => {
                              if (membersDialogProject) {
                                void handleRemoveMemberFromProject(membersDialogProject.id, member.userId);
                              }
                            }}
                          >
                            Remove
                          </Button>
                        ) : (
                          <Typography variant="body2" color="text.secondary">-</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ flexShrink: 0, px: 3, pb: 2 }}>
          <Button onClick={() => setMembersDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Projects; 